import { useEffect, useRef, useState } from "react";

/**
 * True once the page has finished loading and the browser has a spare moment.
 *
 * Third-party embeds mount behind this so they stop competing with the site's
 * own first render. The gap is a fraction of a second and the embed's
 * placeholder already reserves its height, so nothing moves when it lands.
 */
export function useAfterLoad(timeout = 2000) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let idleId: number | undefined;

    const schedule = () => {
      if (typeof requestIdleCallback === "function") {
        idleId = requestIdleCallback(() => setReady(true), { timeout });
      } else {
        idleId = window.setTimeout(() => setReady(true), 300);
      }
    };

    // Already loaded — nothing to yield to. This is the case when the quote
    // modal opens long after page load, and its form should appear at once
    // rather than waiting on an idle callback.
    if (document.readyState === "complete") {
      setReady(true);
      return;
    }
    window.addEventListener("load", schedule, { once: true });
    return () => {
      window.removeEventListener("load", schedule);
      cancel(idleId);
    };
  }, [timeout]);

  return ready;
}

function cancel(id: number | undefined) {
  if (id === undefined) return;
  if (typeof cancelIdleCallback === "function") cancelIdleCallback(id);
  else clearTimeout(id);
}

/**
 * Reports once the referenced element has come within `rootMargin` of the
 * viewport, and then stops observing.
 *
 * Used to hold back the heavy third-party embeds — the map, the reviews
 * widget, the estimate form — until a visitor is actually on their way to
 * them. Anything already on screen reports true on the very first observer
 * callback, so this alone never delays visible content; callers that also
 * want to stay out of the initial load pair it with useAfterLoad.
 *
 * Browsers without IntersectionObserver fall back to "visible", which keeps
 * the old always-mount behaviour rather than hiding content.
 */
export function useInView<T extends Element>(rootMargin = "300px") {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView, rootMargin]);

  return { ref, inView };
}
